/**
 * Player Ecosystem service — career dashboard, search, linking, fixture→history.
 * Sits above Identity / Profiles / Match History (ADR-028 unchanged).
 */

import * as playerRepository from "../repositories/playerRepository";
import * as arenaRepository from "../repositories/arenaRepository";
import { auth } from "../firebase/config";
import {
  ensureMyPlayer,
  getPublicProfileView,
  buildProfileViewModel,
} from "./playerProfileService";
import { claimPlayer, searchPlayers, resolvePlayer } from "./identityService";
import {
  getPlayerMatchHistoryCards,
  persistCompletedMatch,
} from "./matchHistoryService";
import { buildFixtureMatchHistoryInput } from "../domain/player/buildFixtureMatchHistory";
import {
  projectPlayerLockedDrawBanners,
  projectPlayerNextFixture,
} from "../domain/player/projectPlayerNextFixture";
import { hydrateCompetitiveState } from "./tournamentEventState";
import {
  calculateCareerStatistics,
  projectAchievements,
  projectTournamentHistory,
  filterPlayersByFacets,
  filterClaimedRallyAllyPlayers,
  dedupePlayersByAccount,
  paginateItems,
  inferFavoriteEvents,
} from "../domain/player/playerCareer";
import { collectPlayerIdsFromArenasForSport } from "../domain/sport/sportDiscoverScope";
import {
  SPORT_FILTER_OVERALL,
  resolveHistorySportId,
  calculateStatsForSportFilter,
  projectAchievementsForSportFilter,
  buildSportSwitcherOptions,
  buildCrossSportSummary,
  projectPersonalRecords,
  projectActivityTimeline,
  buildCareerStatTiles,
} from "../domain/player/playerIntelligence";
import {
  projectAchievementFramework,
  projectCareerMilestones,
  buildActivityCalendar,
  projectPersonalBests,
  projectStoryTimeline,
  buildIntelligenceEmptyCopy,
  projectBadmintonSportAchievements,
  projectTennisSportAchievements,
  projectPickleballSportAchievements,
  projectTableTennisSportAchievements,
} from "../domain/player/playerAchievementFramework";
import { resolveSportModule } from "../sports";
import { normalizeSportId, DEFAULT_SPORT, SPORT_IDS } from "../domain/sport/sportTypes";
import { COMPETITION_STATUSES } from "../domain/competition/competitionTypes";
import { listOrganizationBadgesForUser } from "./organizationService";

function collectSportAchievements(sportFilter, cards) {
  if (!sportFilter || sportFilter === SPORT_FILTER_OVERALL) {
    // Overall: merge unlocked sport badges from any playable provider (dedupe by id).
    const providers = [
      projectBadmintonSportAchievements,
      projectTennisSportAchievements,
      projectPickleballSportAchievements,
      projectTableTennisSportAchievements,
    ];
    const byId = new Map();
    providers.forEach((fn) => {
      fn(cards).forEach((a) => {
        const prev = byId.get(a.id);
        if (!prev || (a.unlocked && !prev.unlocked)) byId.set(a.id, a);
      });
    });
    // Only surface unlocked on Overall to avoid four locked sport rows cluttering.
    return [...byId.values()].filter((a) => a.unlocked);
  }
  const sportId = normalizeSportId(sportFilter);
  const provider =
    resolveSportModule(sportId)?.createStatisticsProvider?.() ||
    resolveSportModule(sportId)?.createStatsProvider?.();
  if (provider?.projectSportAchievements) {
    return provider.projectSportAchievements(cards) || [];
  }
  // Fallback map if provider not wired
  if (sportId === SPORT_IDS.BADMINTON) return projectBadmintonSportAchievements(cards);
  if (sportId === SPORT_IDS.TENNIS) return projectTennisSportAchievements(cards);
  if (sportId === SPORT_IDS.PICKLEBALL) return projectPickleballSportAchievements(cards);
  if (sportId === SPORT_IDS.TABLE_TENNIS) return projectTableTennisSportAchievements(cards);
  return [];
}

function currentUid() {
  return auth.currentUser?.uid || null;
}

/**
 * Persist tournament fixture result into matchRecords / matchParticipants (idempotent).
 * @param {object} input
 * @param {object} input.competition
 * @param {object} input.draw
 * @param {object} input.fixture
 * @param {string} [input.createdBy]
 */
export async function accumulateFixtureMatchHistory(input) {
  const createdBy = input.createdBy || currentUid();
  if (!createdBy) return null;
  try {
    const payload = buildFixtureMatchHistoryInput({
      competition: input.competition,
      draw: input.draw,
      fixture: input.fixture,
      createdBy,
    });
    return await persistCompletedMatch(payload);
  } catch (err) {
    console.warn("[playerEcosystem] accumulateFixtureMatchHistory failed", err);
    return null;
  }
}

/**
 * Link an unclaimed (or self) player profile to the signed-in user.
 * @param {string} playerId
 */
export async function linkPlayerToAccount(playerId) {
  const uid = currentUid();
  if (!uid) throw new Error("Sign in required.");
  const player = await resolvePlayer(playerId);
  if (!player) throw new Error("Player not found.");
  if (player.userId && player.userId !== uid) {
    throw new Error("This player is already linked to another account.");
  }
  return claimPlayer(playerId, uid);
}

/**
 * Search players by name + optional club / country / state facets.
 * Discover directory: claimed RallyAlly accounts only, one row per Auth uid,
 * viewer excluded, most recent profile name wins on duplicates.
 * When `sportId` is set, only players linked to that sport via Arena entries
 * (and optional leaderboard sample) are returned.
 * @param {object} options
 */
export async function searchPlayerDirectory(options = {}) {
  const {
    query = "",
    club = "",
    country = "",
    state = "",
    sportId = "",
    limit = 20,
    offset = 0,
    excludeUserId = auth.currentUser?.uid || null,
    claimedAccountsOnly = true,
  } = options;

  // Fetch a wider page then facet-filter + paginate in memory.
  const fetchLimit = Math.min(50, Math.max(limit * 3, 25));
  const raw = await searchPlayers(query || club || country || state || "", fetchLimit);
  let filtered = filterPlayersByFacets(raw, { club, country, state });
  if (claimedAccountsOnly) {
    filtered = filterClaimedRallyAllyPlayers(filtered);
    filtered = dedupePlayersByAccount(filtered);
  }
  if (excludeUserId) {
    filtered = filtered.filter((p) => p.userId !== excludeUserId);
  }

  if (sportId) {
    const arenas = await arenaRepository.listRecentArenas({ max: 80 });
    const allowed = collectPlayerIdsFromArenasForSport(arenas, sportId);
    try {
      const { getPublicLeaderboard } = await import("./rankingService");
      const board = await getPublicLeaderboard({ sportId, limit: 40 });
      (board?.players || []).forEach((row) => {
        if (row?.playerId) allowed.add(String(row.playerId));
      });
    } catch {
      /* rankings optional for Discover scope */
    }
    filtered = filtered.filter((p) => allowed.has(String(p.playerId)));
  }

  const page = paginateItems(filtered, { limit, offset });

  return {
    ...page,
    items: page.items.map((p) => ({
      playerId: p.playerId,
      displayName: p.displayName,
      club: p.club || null,
      city: p.city || null,
      state: p.state || null,
      country: p.country || null,
      countryIso: p.countryIso || null,
      preferredHand: p.preferredHand || "",
      profilePhoto: p.profilePhoto || null,
      isProfilePublic: p.isProfilePublic !== false,
      userId: p.userId || null,
    })),
  };
}

function isTournamentArena(competition) {
  const mode = String(competition?.mode || "").toLowerCase();
  const modules = Array.isArray(competition?.enabledModules) ? competition.enabledModules : [];
  return mode === "tournament" || modules.includes("tournament");
}

/**
 * Draws and entries live on the event, not the arena shell.
 * Home must hydrate tournament arenas or a locked draw never becomes a banner.
 * @param {object[]} competitions
 */
async function withPlayerDraws(competitions) {
  return Promise.all(
    (competitions || []).map(async (competition) => {
      const hasEntries = Array.isArray(competition?.managedEntries) && competition.managedEntries.length > 0;
      const hasDraws = Array.isArray(competition?.managedDraws) && competition.managedDraws.length > 0;
      if ((hasEntries && hasDraws) || !isTournamentArena(competition)) return competition;
      try {
        return (await hydrateCompetitiveState(competition)) || competition;
      } catch {
        return competition;
      }
    })
  );
}

/**
 * Registrations for a player from recent competitions (memberPlayerIds).
 * @param {string} playerId
 * @param {{ maxCompetitions?: number }} [options]
 */
export async function listPlayerRegistrations(playerId, options = {}) {
  if (!playerId) return { current: [], upcoming: [] };
  const max = Math.min(options.maxCompetitions || 40, 80);
  const competitions = await withPlayerDraws(
    await arenaRepository.listRecentCompetitions({ max })
  );
  const openStatuses = new Set([
    COMPETITION_STATUSES.PUBLISHED,
    COMPETITION_STATUSES.IN_PROGRESS,
    COMPETITION_STATUSES.REGISTRATION_OPEN,
    "published",
    "in_progress",
    "registration_open",
  ]);

  const current = [];
  const upcoming = [];

  competitions.forEach((c) => {
    const entries = (c.managedEntries || []).filter((e) =>
      (e.memberPlayerIds || []).includes(playerId)
    );
    if (!entries.length) return;
    const eventNames = (c.managedEvents || [])
      .filter((ev) => entries.some((e) => e.eventId === ev.eventId))
      .map((ev) => ev.name)
      .filter(Boolean);
    const row = {
      competitionId: c.competitionId || c.id,
      name: c.name || "Tournament",
      status: c.status,
      entries: entries.map((e) => ({
        entryId: e.entryId,
        eventId: e.eventId,
        label: e.label,
        status: e.status,
      })),
      eventNames,
      dates: c.dates || null,
    };
    if (openStatuses.has(c.status) || c.status === COMPETITION_STATUSES.DRAFT) {
      if (c.status === COMPETITION_STATUSES.IN_PROGRESS || c.status === "in_progress") {
        current.push(row);
      } else {
        upcoming.push(row);
      }
    }
  });

  const nextFixture = projectPlayerNextFixture(competitions, playerId);
  const drawBanners = projectPlayerLockedDrawBanners(competitions, playerId);
  return { current, upcoming, nextFixture, drawBanners };
}

/**
 * Owner player dashboard projection (Player Intelligence Foundation).
 * @param {string} userId
 * @param {{
 *   historyLimit?: number,
 *   skipCompetitionNames?: boolean,
 *   maxRegistrationCompetitions?: number,
 *   recentLimit?: number,
 *   sportId?: string,
 *   sportFilter?: string,
 * }} [options]
 * `sportFilter` / `sportId`: sport id, or `"overall"` for cross-sport aggregation.
 */
export async function getMyPlayerDashboard(userId, options = {}) {
  const historyLimit = Math.min(Math.max(options.historyLimit ?? 50, 1), 80);
  const skipCompetitionNames = Boolean(options.skipCompetitionNames);
  const maxRegistrationCompetitions = options.maxRegistrationCompetitions;
  const recentLimit = Math.min(Math.max(options.recentLimit ?? 8, 1), 20);

  const player = await ensureMyPlayer(userId);
  const profile = buildProfileViewModel(player, { viewerUserId: userId, mode: "owner" });

  const sportFilterRaw = options.sportFilter ?? options.sportId ?? SPORT_FILTER_OVERALL;
  const sportFilter =
    sportFilterRaw === SPORT_FILTER_OVERALL
      ? SPORT_FILTER_OVERALL
      : normalizeSportId(sportFilterRaw);
  const historySportId = resolveHistorySportId(sportFilter);

  // Overall switcher needs multi-sport sample; fetch a wider unfiltered page once.
  const [historyResult, allSportsResult, registrations, organizationBadges] = await Promise.all([
    getPlayerMatchHistoryCards({
      playerId: player.playerId,
      limit: historyLimit,
      sportId: historySportId || undefined,
    }),
    sportFilter === SPORT_FILTER_OVERALL
      ? Promise.resolve(null)
      : getPlayerMatchHistoryCards({
          playerId: player.playerId,
          limit: Math.min(historyLimit, 40),
        }),
    listPlayerRegistrations(player.playerId, {
      maxCompetitions: maxRegistrationCompetitions,
    }),
    listOrganizationBadgesForUser(userId),
  ]);

  const cards = historyResult.items || [];
  const switcherCards =
    sportFilter === SPORT_FILTER_OVERALL ? cards : allSportsResult?.items || cards;

  const statsProvider =
    sportFilter !== SPORT_FILTER_OVERALL
      ? resolveSportModule(sportFilter)?.createStatisticsProvider?.() ||
        resolveSportModule(sportFilter)?.createStatsProvider?.()
      : null;
  const stats = calculateStatsForSportFilter(sportFilter, cards);

  /** @type {Record<string, string>} */
  const names = {};
  if (!skipCompetitionNames) {
    const competitionIds = [...new Set(cards.map((c) => c.competitionId).filter(Boolean))];
    await Promise.all(
      competitionIds.map(async (id) => {
        try {
          const doc = await arenaRepository.fetchCompetition(id);
          if (doc?.name) names[id] = doc.name;
        } catch {
          /* ignore */
        }
      })
    );
  }
  const tournamentHistory = skipCompetitionNames
    ? []
    : (statsProvider?.projectTournamentHistory || projectTournamentHistory)(cards, names);
  const legacyAchievements = projectAchievementsForSportFilter(sportFilter, stats, cards).slice(0, 8);
  const sportAchievements = collectSportAchievements(sportFilter, cards);
  const achievementFramework = projectAchievementFramework(stats, cards, sportAchievements);
  const milestones = projectCareerMilestones(stats, cards);
  const activityCalendar = buildActivityCalendar(cards);
  const personalBests = projectPersonalBests(stats, cards, sportFilter);
  const activityTimeline = projectStoryTimeline(
    cards,
    stats,
    achievementFramework,
    milestones
  );
  // Keep legacy feed helper available for older callers
  const legacyTimeline = projectActivityTimeline(cards, stats, legacyAchievements);
  const favoriteEvents =
    Array.isArray(player.preferredEvents) && player.preferredEvents.length
      ? player.preferredEvents
      : (statsProvider?.inferFavoriteEvents || inferFavoriteEvents)(cards);

  const personalRecords = projectPersonalRecords(stats, cards);
  const crossSport = buildCrossSportSummary(switcherCards);
  const sportSwitcher = buildSportSwitcherOptions(switcherCards);
  const primarySport =
    crossSport.favouriteSport?.sportId ||
    (sportFilter !== SPORT_FILTER_OVERALL ? sportFilter : DEFAULT_SPORT);
  const sportLabel =
    sportFilter === SPORT_FILTER_OVERALL
      ? null
      : resolveSportModule(sportFilter)?.displayName || sportFilter;
  const emptyCopy = buildIntelligenceEmptyCopy({
    sportFilter,
    sportLabel,
    matchesPlayed: stats.matchesPlayed,
  });

  return {
    profile: {
      ...profile,
      club: player.club || null,
      state: player.state || null,
      dateOfBirth: player.dateOfBirth || null,
      preferredEvents: player.preferredEvents || [],
      userId: player.userId || null,
      primarySportId: primarySport,
      primarySportLabel:
        resolveSportModule(primarySport)?.displayName || primarySport,
    },
    sportFilter,
    sportSwitcher,
    crossSport,
    stats,
    statTiles: buildCareerStatTiles(stats),
    recentMatches: cards.slice(0, recentLimit),
    activityTimeline,
    legacyTimeline,
    personalRecords,
    personalBests,
    milestones,
    activityCalendar,
    achievementFramework,
    emptyCopy,
    tournamentHistory,
    achievements: achievementFramework.unlocked.slice(0, 8),
    registrations,
    favoriteEvents,
    organizationBadges,
    linked: Boolean(player.userId === userId),
  };
}

/**
 * Slim Home dashboard — fewer history reads, no competition-name N+1, parallel side loads.
 * Scoped to Active Sport when `sportFilter` / `sportId` is provided (Global Sport Mode).
 * @param {string} userId
 * @param {{ sportFilter?: string, sportId?: string }} [options]
 */
export async function getPlayerHomeDashboard(userId, options = {}) {
  const sportFilter = options.sportFilter ?? options.sportId;
  return getMyPlayerDashboard(userId, {
    historyLimit: 24,
    skipCompetitionNames: true,
    maxRegistrationCompetitions: 20,
    recentLimit: 8,
    ...(sportFilter ? { sportFilter } : {}),
  });
}

/**
 * Public career view — hides private contact fields (handled by profile view).
 * @param {string} playerId
 * @param {string | null} [viewerUserId]
 * @param {{ sportFilter?: string }} [options]
 */
export async function getPublicPlayerCareer(playerId, viewerUserId = null, options = {}) {
  const profile = await getPublicProfileView(playerId, viewerUserId);
  if (!profile) return null;
  if (profile.isPrivate && !profile.canEdit) {
    return { profile, private: true };
  }

  const player = await resolvePlayer(playerId);
  const sportFilter = options.sportFilter || SPORT_FILTER_OVERALL;
  const historySportId = resolveHistorySportId(sportFilter);
  const { items: cards } = await getPlayerMatchHistoryCards({
    playerId,
    limit: 40,
    sportId: historySportId || undefined,
  });
  const stats = calculateStatsForSportFilter(sportFilter, cards);
  const competitionIds = [...new Set(cards.map((c) => c.competitionId).filter(Boolean))];
  /** @type {Record<string, string>} */
  const names = {};
  await Promise.all(
    competitionIds.slice(0, 20).map(async (id) => {
      try {
        const doc = await arenaRepository.fetchCompetition(id);
        if (doc?.name) names[id] = doc.name;
      } catch {
        /* ignore */
      }
    })
  );

  const sportAchievements = collectSportAchievements(sportFilter, cards);
  const achievementFramework = projectAchievementFramework(stats, cards, sportAchievements);
  const milestones = projectCareerMilestones(stats, cards);
  const activityCalendar = buildActivityCalendar(cards);
  const personalBests = projectPersonalBests(stats, cards, sportFilter);

  return {
    profile: {
      ...profile,
      club: player?.club || null,
      state: player?.state || null,
      preferredEvents: player?.preferredEvents || [],
      preferredHand: profile.preferredHand || player?.preferredHand || "",
    },
    private: false,
    sportFilter,
    sportSwitcher: buildSportSwitcherOptions(cards),
    crossSport: buildCrossSportSummary(cards),
    stats,
    statTiles: buildCareerStatTiles(stats),
    recentMatches: cards.slice(0, 8),
    activityTimeline: projectStoryTimeline(cards, stats, achievementFramework, milestones),
    personalRecords: projectPersonalRecords(stats, cards),
    personalBests,
    milestones,
    activityCalendar,
    achievementFramework,
    emptyCopy: buildIntelligenceEmptyCopy({
      sportFilter,
      sportLabel:
        sportFilter === SPORT_FILTER_OVERALL
          ? null
          : resolveSportModule(sportFilter)?.displayName || sportFilter,
      matchesPlayed: stats.matchesPlayed,
    }),
    tournamentHistory: projectTournamentHistory(cards, names),
    achievements: achievementFramework.unlocked.slice(0, 8),
    favoriteEvents:
      Array.isArray(player?.preferredEvents) && player.preferredEvents.length
        ? player.preferredEvents
        : inferFavoriteEvents(cards),
    organizationBadges: await listOrganizationBadgesForUser(player?.userId || null),
  };
}

export {
  calculateCareerStatistics,
  projectTournamentHistory,
  projectAchievements,
  filterPlayersByFacets,
  filterClaimedRallyAllyPlayers,
  dedupePlayersByAccount,
  SPORT_FILTER_OVERALL,
};
